import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  useTheme,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import {
  NotificationStage,
  NotificationTemplate,
  OrganisationSettings,
} from '../../../../interfaces/delivery-company/orders';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { OrgSettingsContext } from '../../../../contexts/org-settings-context';
import { formatMessageWithLineBreaks, ORG_STAGES } from './utils';
import { styled } from '@mui/material/styles';
import { useSnackBar } from '../../../../contexts/snackbar';
import { grey } from '@mui/material/colors';
import { NotificationTemplateStatus } from '../../../../enums/notifications';
import TemplateDialog from './template-input';

interface NotificationManagerTemplatesProps {
  logoUrl?: string;
  notification_templates: NotificationTemplate[];
  notification_template_status?: NotificationTemplateStatus;
  singleTemplateMode?: boolean;
  stages?: NotificationStage[];
}

// Vibrant color palette for templates
const TEMPLATE_COLORS = [
  '#FF5252',
  '#FF4081',
  '#E040FB',
  '#7C4DFF',
  '#536DFE',
  '#448AFF',
  '#40C4FF',
  '#18FFFF',
  '#64FFDA',
  '#69F0AE',
  '#B2FF59',
  '#EEFF41',
  '#FFD740',
  '#FFAB40',
  '#FF6E40',
];

// Function to assign consistent color to each template
const getTemplateColor = (name: string): string => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return TEMPLATE_COLORS[Math.abs(hash) % TEMPLATE_COLORS.length];
};

// StyledCard with color support
const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'templateColor',
})<{ templateColor?: string }>(({ theme, templateColor }) => ({
  marginBottom: theme.spacing(1),
  position: 'relative',
  transition: 'all 0.3s ease',
  backgroundColor: grey[50],
  borderLeft: templateColor ? `10px solid ${templateColor}` : undefined,
  minHeight: '120px',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px) scale(1.01)',
    boxShadow: theme.shadows[4],
  },
  '&.dragging': {
    boxShadow: theme.shadows[6],
    transform: 'scale(1.03)',
  },
}));

function NotificationManagerTemplates({
  logoUrl,
  notification_template_status,
  notification_templates,
  singleTemplateMode = false,
  stages = ORG_STAGES,
}: NotificationManagerTemplatesProps) {
  const { user, config } = useContext(AuthContext);
  const { color: orgColor } = useContext(OrgSettingsContext);
  const theme = useTheme();
  const { showMessage } = useSnackBar();

  const [templates, setTemplates] = useState<NotificationTemplate[]>(
    notification_templates || []
  );
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<NotificationTemplate | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleAdd = () => {
    // In single template mode, if template exists, edit it instead
    if (singleTemplateMode && templates.length > 0) {
      handleEdit(templates[0]);
      return;
    }
    setEditingTemplate(null);
    setPreviewContent('');
    setOpenDialog(true);
  };

  const handleEdit = (template: NotificationTemplate) => {
    setEditingTemplate(template);
    setPreviewContent(template.template);
    setOpenDialog(true);
  };

  const handleDelete = (
    template: NotificationTemplate,
    stage: NotificationStage
  ) => {
    const mutator = (prev: NotificationTemplate[]) => {
      // Count how many stages this template appears in
      const stageCount = stages.filter(
        (s) =>
          prev.find((t) => t.name === template.name)?.orderWithinStages?.[s] !==
          undefined
      ).length;

      if (stageCount <= 1) {
        // If this is the only stage, remove the template completely
        return prev.filter((t) => t.name !== template.name);
      } else {
        // Otherwise just remove from current stage
        return prev.map((t) => {
          if (t.name === template.name) {
            const newOrder = { ...t.orderWithinStages };
            delete newOrder[stage];
            return {
              ...t,
              orderWithinStages: newOrder,
            };
          }
          return t;
        });
      }
    };
    handleSubmit(mutator(templates));
  };

  const handleSave = async (template: NotificationTemplate) => {
    const mutator = (prev: NotificationTemplate[]) => {
      // In single template mode, replace the existing template
      if (singleTemplateMode) {
        return [template];
      }

      const existingIndex = prev.findIndex((t) => t === editingTemplate);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = template;
        return updated;
      }
      return [...prev, template];
    };
    handleSubmit(mutator(templates));
    setOpenDialog(false);
  };

  const handleSubmit = async (templates: NotificationTemplate[]) => {
    setError(null);
    setSuccessMessage(null);
    setUploading(true);

    try {
      const requestData = { notification_templates: JSON.stringify(templates) };
      await axios.post(`${API_ENDPOINT}/api/v1/settings`, requestData, config);
      showMessage('Saved Templates');
      setTemplates(templates);
    } catch (err) {
      setError('Failed to save templates. Please try again.');
      showMessage('Failed to save templates. Please try again.', 'error');
    }

    setUploading(false);
  };

  const handleStageDragEnd = (stage: NotificationStage, result: any) => {
    if (
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    setTemplates((prevTemplates) => {
      const newTemplates = [...prevTemplates];
      const stageTemplates = newTemplates
        .filter((t) => t.orderWithinStages?.[stage] !== undefined)
        .sort(
          (a, b) =>
            (a.orderWithinStages[stage] ?? 0) -
            (b.orderWithinStages[stage] ?? 0)
        );

      const [movedTemplate] = stageTemplates.splice(result.source.index, 1);
      stageTemplates.splice(result.destination.index, 0, movedTemplate);

      stageTemplates.forEach((template, index) => {
        const originalIndex = newTemplates.findIndex((t) => t === template);
        if (originalIndex >= 0) {
          newTemplates[originalIndex] = {
            ...template,
            orderWithinStages: {
              ...template.orderWithinStages,
              [stage]: index,
            },
          };
        }
      });

      return newTemplates;
    });
  };

  const groupedTemplates: Partial<Record<NotificationStage, NotificationTemplate[]>> = {};

  stages.forEach(stage => {
    groupedTemplates[stage] = [];
  });

  templates.forEach((template) => {
    stages.forEach((stage) => {
      if (
        template.orderWithinStages &&
        template.orderWithinStages[stage] !== undefined
      ) {
        groupedTemplates[stage]?.push(template);
      }
    });
  });

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        {singleTemplateMode ? 'Customer Notification Template' : 'Driver Notification Templates'}
      </Typography>

      {singleTemplateMode && templates.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Create a custom notification template to be sent to your customers when orders are delivered.
        </Alert>
      )}

      {(singleTemplateMode && templates.length == 0) &&  <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleAdd}
        sx={{ mb: 2 }}
      >
        {'Add New Template'}
      </Button>}

      {!singleTemplateMode && (
        <Button
          variant="contained"
          onClick={() => handleSubmit(templates)}
          sx={{ mb: 2, ml: 2 }}
          disabled={uploading}
        >
          {uploading ? <CircularProgress size={24} /> : 'Save Templates'}
        </Button>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {singleTemplateMode ? (
        // Single template mode: Show simple card without drag-and-drop
        templates.length > 0 ? (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              {notification_template_status && (
                <Chip
                  label={notification_template_status === NotificationTemplateStatus.APPROVED ? 'Approved' : 'Under Review'}
                  color={notification_template_status === NotificationTemplateStatus.APPROVED ? 'success' : 'warning'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {templates[0].name}
                </Typography>
                <div>
                  <Tooltip title="Edit">
                    <IconButton onClick={() => handleEdit(templates[0])} size="small">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      onClick={() => handleDelete(templates[0], stages[0])}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  color: 'text.secondary',
                  whiteSpace: 'pre-wrap',
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                }}
              >
                {templates[0].template}
              </Typography>
            </CardContent>
          </Card>
        ) : null
      ) : (
        // Multi-template mode: Show drag-and-drop grid
        <Grid container spacing={2}>
          {stages.map((stage) => (
            <Grid item xs={12} md={4} key={stage}>
              <Typography variant="h6" gutterBottom>
                {stage}
              </Typography>
              <DragDropContext
                onDragEnd={(result) => handleStageDragEnd(stage, result)}
              >
                <Droppable droppableId={stage}>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {groupedTemplates[stage]
                        ?.sort(
                          (a, b) =>
                            (a.orderWithinStages[stage] ?? 0) -
                            (b.orderWithinStages[stage] ?? 0)
                        )
                        .map((template, index) => (
                          <Draggable
                            key={`${stage}-${template.name}`}
                            draggableId={`${stage}-${template.name}`}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                              >
                                <StyledCard
                                  templateColor={getTemplateColor(template.name)}
                                  className={
                                    snapshot.isDragging ? 'dragging' : ''
                                  }
                                  sx={{
                                    opacity: snapshot.isDragging ? 0.8 : 1,
                                  }}
                                >
                                  <CardContent>
                                    <div
                                      style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <div {...provided.dragHandleProps}>
                                        <DragIndicatorIcon color="action" />
                                      </div>
                                      <Typography
                                        variant="subtitle1"
                                        sx={{
                                          ml: 1,
                                          flexGrow: 1,
                                          fontWeight: 'bold',
                                          color: 'text.primary',
                                        }}
                                      >
                                        {template.name}
                                      </Typography>
                                      <Tooltip title="Edit">
                                        <IconButton
                                          onClick={() => handleEdit(template)}
                                          size="small"
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>

                                      <Tooltip title="Delete">
                                        <IconButton
                                          onClick={() =>
                                            handleDelete(template, stage)
                                          }
                                          size="small"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </div>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        mt: 1,
                                        color: 'text.secondary',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                      }}
                                    >
                                      {template.template}
                                    </Typography>
                                    <div
                                      style={{
                                        display: 'flex',
                                        gap: 4,
                                        marginTop: 8,
                                        flexWrap: 'wrap',
                                      }}
                                    >
                                      {Object.entries(
                                        template.orderWithinStages || {}
                                      ).map(([stage]) => (
                                        <span
                                          key={stage}
                                          style={{
                                            fontSize: '0.7rem',
                                            padding: '2px 6px',
                                            borderRadius: 4,
                                            backgroundColor:
                                              theme.palette.grey[200],
                                            color: theme.palette.text.secondary,
                                          }}
                                        >
                                          {stage}
                                        </span>
                                      ))}
                                    </div>
                                  </CardContent>
                                </StyledCard>
                              </div>
                            )}
                          </Draggable>
                        ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </Grid>
          ))}
        </Grid>
      )}

      {openDialog && (
        <TemplateDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSave={handleSave}
          initialData={editingTemplate}
          previewContent={previewContent}
          onContentChange={setPreviewContent}
          logoUrl={logoUrl}
          color={orgColor}
          stages={stages}
          singleTemplateMode={singleTemplateMode}
        />
      )}
    </div>
  );
}
export default NotificationManagerTemplates;
